// Angular
import { Inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';

// RCCL Libraries
import {
  TELEMETRY_HTTP_CLIENT,
  TelemetryHttpClient,
} from '@rccl/telemetry-angular';

// RXJS
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Miscellaneous
import * as utils from '../../../util';
import { ErrorService } from './error.service';
import { GatingServices } from './gating.services';
import { TelemetryErrorData } from '../../../shared/interfaces';
import { analyticsConstants, constants } from '../../../constants';
import {
  APIGEE_APP_KEY,
  BRAND,
  WHITE_LIST_DOMAINS,
} from '~environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HttpRepositoryService {
  constructor(
    @Inject(TELEMETRY_HTTP_CLIENT) private readonly httpClient: TelemetryHttpClient,
    private readonly gatingService: GatingServices,
    private readonly errorService: ErrorService
  ) {}

  get brand() {
    return BRAND;
  }

  get vdsId() {
    const vds = this.gatingService.getLoggedInGuestInfo();
    return vds.vdsId;
  }

  get locale() {
    return this.gatingService.getReservationData()?.locale;
  }

  private microServicesHeaders() {
    return {
      headers: new HttpHeaders({
        AppKey: APIGEE_APP_KEY,
        'Access-Token': utils.readCookie(constants.COOKIES.OLCI.ACCESS_TOKEN),
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      }),
    };
  }

  private olciServicesHeaders() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: 'Sat, 01 Jan 2030 00:00:00 GMT',
        'X-CHANNEL': 'web',
        'Access-Control-Allow-Origin': [...WHITE_LIST_DOMAINS],
        'Access-Control-Allow-Credentials': 'true',
        'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
        'Access-Control-Allow-Headers':
          'Origin, Content-Type, Accept, X-Custom-Header, Upgrade-Insecure-Requests',
      }),
    };
  }

  public headers(
    isOlciService = false,
    queryParams?: HttpParams,
    customHeaders?: Record<string, string>
  ): { headers: HttpHeaders; params?: HttpParams } {
    const baseHeaders = isOlciService
      ? this.olciServicesHeaders()
      : this.microServicesHeaders();

    let headers = baseHeaders.headers;

    if (customHeaders) {
      Object.entries(customHeaders).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
    }

    return queryParams ? { headers, params: queryParams } : { headers };
  }

  private baseGet<T>(
    url: string,
    options: { headers: HttpHeaders },
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA
  ): Observable<any> {
    return this.httpClient
      .get<T>(url, options)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorService.defaultTelemetryHttpCatch(
            error,
            telemetryErrorData,
            url
          )
        )
      );
  }

  private olciCustomGet(
    url: string,
    options: { headers: HttpHeaders },
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA
  ): Observable<any> {
    return this.baseGet(url, options, telemetryErrorData).pipe(
      map((res: any) => res)
    );
  }

  public get<T>(
    url: string,
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA,
    isOlciService = false,
    queryParams?: HttpParams,
    customHeaders?: Record<string, string>
  ): Observable<any> {
    const headers = this.headers(isOlciService, queryParams, customHeaders);

    return isOlciService
      ? this.olciCustomGet(url, headers, telemetryErrorData)
      : this.baseGet<T>(url, headers, telemetryErrorData);
  }

  private basePost<T>(
    url: any,
    body: any,
    options: { headers: HttpHeaders },
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA
  ): Observable<T> {
    return this.httpClient
      .post<T>(url, body, options)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorService.defaultTelemetryHttpCatch(
            error,
            telemetryErrorData,
            url
          )
        )
      );
  }

  private basePut<T>(
    url: any,
    body: any,
    options: { headers: HttpHeaders },
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA
  ): Observable<T> {
    return this.httpClient
      .put<T>(url, body, options)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorService.defaultTelemetryHttpCatch(
            error,
            telemetryErrorData,
            url
          )
        )
      );
  }

  private olciCustomPut(
    url: any,
    body: any,
    options: { headers: HttpHeaders },
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA
  ): Observable<any> {
    return this.basePut(url, body, options, telemetryErrorData).pipe(
      map((res: any) => res)
    );
  }

  private olciCustomPost(
    url: any,
    body: any,
    options: { headers: HttpHeaders },
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA
  ): Observable<any> {
    return this.basePost(url, body, options, telemetryErrorData).pipe(
      map((res: any) => res)
    );
  }

  public post<T>(
    url: any,
    body: any,
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA,
    isOlciService = false
  ): Observable<T> {
    const options = this.headers(isOlciService);

    return isOlciService
      ? this.olciCustomPost(url, body, options, telemetryErrorData)
      : this.basePost<T>(url, body, options, telemetryErrorData);
  }

  public put<T>(
    url: any,
    body: any,
    telemetryErrorData: TelemetryErrorData = analyticsConstants.GENERIC_TELEMETRY_ERROR_DATA,
    isOlciService = false
  ): Observable<T> {
    const options = this.headers(isOlciService);

    return isOlciService
      ? this.olciCustomPut(url, body, options, telemetryErrorData)
      : this.basePut<T>(url, body, options, telemetryErrorData);
  }
}
